/**
 * Telemetría REAL por skill (contrato `SkillsTelemetry`): reductor puro de la
 * respuesta de la API a lo que pintan la lista y el banco. Sustituye a la
 * semilla del canon F6 — los números salen ya de las trazas persistidas.
 *
 * IDIOMA DE ESTA TABLA (divergencia consciente del canon, donde «el cero se
 * escribe —»): ahora «—» significa NO SE MIDE, y por eso el cero medido se
 * escribe `0`. Lo que no se puede medir hoy no se inventa:
 *   · `confirmadasPct` null → «—» + glosa «sin propuestas resueltas»;
 *   · conflictos de precedencia → «—» SIEMPRE: la traza no registra qué
 *     OTROS métodos casaron, así que el conflicto no es medible todavía.
 */
import type { SkillTelemetryRow, SkillsTelemetry } from "@skein/contracts";

/** Un skill del catálogo que no aparece en la ventana: ceros honestos. */
export const sinUso = (slug: string): SkillTelemetryRow =>
  ({ slug, casas: 0, disparos: 0, delta: null, propuestas: 0, confirmadasPct: null, ultimoUso: null });

/** Índice slug → fila. Sin telemetría (cargando o caída) devuelve vacío. */
export function indexar(t: SkillsTelemetry | undefined): Map<string, SkillTelemetryRow> {
  return new Map((t?.porSkill ?? []).map((r) => [r.slug, r]));
}
export const filaDe = (idx: Map<string, SkillTelemetryRow>, slug: string): SkillTelemetryRow =>
  idx.get(slug) ?? sinUso(slug);

/** Días de la ventana: el contrato manda ISO (desde/hasta), no el número. */
export function diasVentana(t: SkillsTelemetry | undefined): number | null {
  if (!t) return null;
  const d = Math.round((new Date(t.hasta).getTime() - new Date(t.desde).getTime()) / 86_400_000);
  return Number.isFinite(d) && d > 0 ? d : null;
}
/** «últimos 30 días» — o «la ventana» mientras no se sepa cuál es. */
export const ventana = (t: SkillsTelemetry | undefined): string => {
  const d = diasVentana(t);
  return d === null ? "la ventana" : `últimos ${d} días`;
};

/** El delta de DISPAROS frente a la ventana anterior, con el signo
 *  tipográfico del canon (− U+2212, no el guion). Sin ventana previa o sin
 *  variación no hay chip: un «+0» no dice nada. */
export function deltaTexto(delta: number | null): string | null {
  if (delta === null || delta === 0) return null;
  return delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`;
}
export const deltaTono = (delta: number): "good" | "bad" => (delta > 0 ? "good" : "bad");

/** Clave de ordenación de la tabla → valor comparable. `confirmadasPct` null
 *  NO es cero: se ordena por debajo del 0 % real (−1), porque «no hay
 *  propuestas resueltas» y «ninguna confirmada» no son la misma noticia. */
export type ClaveOrden = "nombre" | "casas" | "disparos" | "conf";
export function valorOrden(s: { nombre: string; t: SkillTelemetryRow }, k: ClaveOrden): string | number {
  if (k === "nombre") return s.nombre;
  if (k === "conf") return s.t.confirmadasPct ?? -1;
  return s.t[k];
}

/** La señal para el curador: respuestas que no encontraron método en la
 *  ventana, sobre el total. Sin telemetría NO hay señal — callar no es
 *  «cero sin método». */
export function sinMetodo(t: SkillsTelemetry | undefined): { n: number; de: number; dias: number } | null {
  if (!t) return null;
  return { n: t.sinMetodo, de: t.totalRespuestas, dias: diasVentana(t) ?? 30 };
}
